import {
  DataGridBox,
  DeleteDialog,
  CustomToolbar,
  ZebraDataGrid,
  PermissionHandler,
  CustomNoRowsOverlay,
} from 'components';
import { useSnackbar } from 'notistack';
import { STORAGE_KEY_ENUM } from 'enums';
import apiService from 'library/api-service';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import { renderDataGridLocaleText } from 'utils';
import {
  selectSalesStageTypes,
  selectSalesStageTypesIsEmpty,
  selectSalesStageTypesLoading,
} from 'store/features/sales-stage-types/selector';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, Grid2 as Grid } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import salesStageTypesActions from 'store/features/sales-stage-types/actions';
import CreateSalesStageTypeDialog from './components/create-sales-stage-type-dialog';
import UpdateSalesStageTypeDialog from './components/update-sales-stage-type-dialog';

const SalesStageTypes = () => {
  // other hooks
  const intl = useIntl();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // redux hooks
  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectSalesStageTypesLoading);
  const permissionCodes = useAppSelector(selectPermissionCodes);
  const salesStageTypes = useAppSelector(selectSalesStageTypes);
  const isEmptySalesStageTypes = useAppSelector(selectSalesStageTypesIsEmpty);

  // useState hooks
  const [openDeleteSalesStageType, setOpenDeleteSalesStageType] = useState(false);
  const [openCreateSalesStageType, setOpenCreateSalesStageType] = useState(false);
  const [openUpdateSalesStageType, setOpenUpdateSalesStageType] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: intl.formatMessage({ id: 'salesStageTypes.title', defaultMessage: 'Adı' }),
        flex: 1,
      },
    ],
    [intl]
  );

  // async helper functions
  const refresh = async () => {
    dispatch(salesStageTypesActions.getSalesStageTypes());
  };

  // sync helper functions
  const handleCloseDeleteSalesStageType = () => setOpenDeleteSalesStageType(false);
  const handleClickOpenDeleteSalesStageType = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenDeleteSalesStageType(true);
  };

  const handleCloseCreateSalesStageType = () => setOpenCreateSalesStageType(false);
  const handleClickOpenCreateSalesStageType = () => setOpenCreateSalesStageType(true);

  const handleCloseUpdateSalesStageType = () => setOpenUpdateSalesStageType(false);
  const handleClickOpenUpdateSalesStageType = () => {
    // single selection control
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    // multi selection control
    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    // If the check has been passed successfully...
    setOpenUpdateSalesStageType(true);
  };

  // useEffect hooks
  useLayoutEffect(() => {
    if (permissionCodes[0] === STORAGE_KEY_ENUM.NON_INITIALIZED) return;

    if (!permissionCodes.includes('list-sales-stage-type')) {
      navigate('/dashboard');
    }
  }, [permissionCodes, navigate]);

  useEffect(() => {
    if (!salesStageTypes.length && !isEmptySalesStageTypes) {
      dispatch(salesStageTypesActions.getSalesStageTypes());
    }
  }, [salesStageTypes, isEmptySalesStageTypes, dispatch]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['add-sales-stage-type']}>
          <Grid size={3}>
            <Button
              onClick={handleClickOpenCreateSalesStageType}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<AddIcon />}
              color="success"
              sx={{
                color: 'white',
                border: 'none',
                '&:hover': { backgroundColor: 'rgb(83, 189, 83, 0.8)', border: 'none', boxShadow: 'none' },
              }}>
              <FormattedMessage id="salesStageTypes.createSalesStageType" defaultMessage="Satış Aşaması Oluştur" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['update-sales-stage-type']}>
          <Grid size={3}>
            <Button
              onClick={handleClickOpenUpdateSalesStageType}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<EditIcon />}
              color="secondary">
              <FormattedMessage id="salesStageTypes.updateSalesStageType" defaultMessage="Satış Aşaması Düzenle" />
            </Button>
          </Grid>
        </PermissionHandler>
        <PermissionHandler requiredPermissionCodes={['delete-sales-stage-type']}>
          <Grid size={3}>
            <Button
              onClick={handleClickOpenDeleteSalesStageType}
              fullWidth
              size="medium"
              variant="contained"
              endIcon={<DeleteIcon />}
              color="error">
              <FormattedMessage id="salesStageTypes.deleteSalesStageType" defaultMessage="Satış Aşaması Sil" />
            </Button>
          </Grid>
        </PermissionHandler>
      </Grid>
      <CreateSalesStageTypeDialog
        open={openCreateSalesStageType}
        handleClose={handleCloseCreateSalesStageType}
        refresh={refresh}
      />
      <UpdateSalesStageTypeDialog
        open={openUpdateSalesStageType}
        salesStageTypeId={rowSelectionModel[0]}
        handleClose={handleCloseUpdateSalesStageType}
        refresh={refresh}
      />
      <DeleteDialog
        open={openDeleteSalesStageType}
        id={rowSelectionModel[0]}
        handleClose={handleCloseDeleteSalesStageType}
        refresh={refresh}
        setRowSelectionModel={setRowSelectionModel}
        title={<FormattedMessage id="salesStageTypes.deleteSalesStageType" defaultMessage="Satış Aşaması Sil" />}
        subTitle={
          <FormattedMessage
            id="salesStageTypes.deleteSalesStageTypeRepeat"
            defaultMessage="Bu işlem onaylandıktan sonra satış aşaması veritabanından silinecektir. Silme işlemini onaylıyor musunuz?"
          />
        }
        action={apiService.deleteSalesStageType}
        successMessage={
          <FormattedMessage
            id="salesStageTypes.deleteSalesStageTypeCompleted"
            defaultMessage="Seçili satış aşamasını silme işlemi başarıyla tamamlandı."
          />
        }
      />
      <br />
      <DataGridBox>
        <ZebraDataGrid
          /* Pagination Settings */
          pagination
          /* Chekbox Settings */
          checkboxSelection
          onRowSelectionModelChange={(newRowSelectionModel) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          /* Remain Settings */
          columns={columns}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          loading={loading}
          localeText={renderDataGridLocaleText()}
          rows={salesStageTypes}
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
            toolbar: () => CustomToolbar({ exportPremiumExcel: true }),
          }}
          disableAggregation
          disableRowGrouping
        />
      </DataGridBox>
    </>
  );
};

export default SalesStageTypes;
